import { useDispatch } from 'react-redux';
import { CreditCard, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { useDeleteCardMutation } from '../../services/orderApi';
import type { SavedCard } from '../../../../types/order.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DeleteCardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardDetails: SavedCard | undefined;
  selectedCard: SavedCard | null;
  setSelectedCard: (card: SavedCard | null) => void;
  onSuccess: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DeleteCardModal({
  open,
  onOpenChange,
  cardDetails,
  selectedCard,
  setSelectedCard,
  onSuccess,
}: DeleteCardModalProps) {
  const dispatch = useDispatch();
  const [deleteCard, { isLoading }] = useDeleteCardMutation();

  if (!cardDetails) return null;

  const cardNumber = `************${cardDetails.cardNumber}`;

  function capitalize(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  async function handleDelete() {
    if (!cardDetails) return;
    try {
      await deleteCard({
        customerProfileId: cardDetails.customerProfileId,
        customerPaymentProfileId: cardDetails.customerPaymentProfileId,
      }).unwrap();

      // Clear selected card if it was the one deleted
      // DV-2789: match on the unique payment-profile id (see usePayment.isCardSelected) — the fuzzy
      // last4+name+expiry tuple could clear a different look-alike card's selection.
      if (
        selectedCard &&
        selectedCard.customerPaymentProfileId === cardDetails.customerPaymentProfileId
      ) {
        setSelectedCard(null);
      }

      onOpenChange(false);
      onSuccess();
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Card removed successfully',
        })
      );
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      const errorMessage = error?.data?.message;
      if (
        errorMessage?.includes(
          'Cannot delete card as there are active orders associated with it.'
        )
      ) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Cannot delete card as there are active orders associated with it.',
          })
        );
      } else if (errorMessage) {
        // Authorize.net (or other payment-processor) failure surfaced via 500.
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Failed to remove card from payment processor. Please try again or contact support.',
          })
        );
      } else {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: 'Failed to delete card',
          })
        );
      }
      onOpenChange(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      srOnlyTitle="Are you sure you want to delete this card?"
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive-soft text-destructive"
          aria-hidden="true"
        >
          <Trash2 className="h-7 w-7" />
        </div>

        <Typography as="h2" variant="heading-lg" className="text-center">
          Are you sure you want to delete this card?
        </Typography>

        <Typography
          as="p"
          variant="body"
          className="text-center text-muted-foreground"
        >
          The card will be removed from your account and you&apos;ll need to
          re-enter the card information to use it for future purchases.
        </Typography>

        <div className="flex w-full flex-col gap-2 rounded-md bg-muted p-3">
          <div className="flex items-center gap-2">
            <CreditCard
              className="h-5 w-5 text-muted-foreground"
              aria-hidden="true"
            />
            <Typography variant="body-medium">Credit card</Typography>
          </div>
          <div className="flex items-center gap-2">
            <Typography variant="body-medium">
              {capitalize(cardDetails.cardType)}
            </Typography>
            <Typography variant="body-medium">{cardNumber}</Typography>
          </div>
        </div>

        <div className="flex w-full flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isLoading}
            onClick={handleDelete}
            className="w-full sm:w-auto"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete card
          </Button>
        </div>
      </div>
    </Modal>
  );
}
